import { useState } from "react";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi } from "../../pages/Segreteria";
import { inputCls, labelCls } from "../legend/ContactForm";

/** Dialog per segnare la tessera come emessa e registrare il codice CONI. */
export const StatoPraticaDialog = ({ tess, onClose, onSaved }) => {
  const [form, setForm] = useState({
    stato: tess?.stato === "tessera_emessa" ? "tessera_emessa" : "pagato",
    codice_coni: tess?.codice_coni || "",
    note: tess?.note_segreteria || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await adminApi.patch(`/admin/tesseramenti/${tess.id}/stato`, form);
      toast.success(form.stato === "tessera_emessa" ? "Tessera segnata come emessa" : "Pratica aggiornata");
      onSaved(data);
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(tess)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stato-dialog" className="max-w-md bg-neutral-950 border-neutral-800 text-neutral-100 rounded-none">
        <DialogHeader className="text-left">
          <div className="mono-label text-xs text-red-500 uppercase tracking-wider">Stato pratica</div>
          <DialogTitle className="font-display text-xl font-black uppercase text-white">
            {tess?.cognome} {tess?.nome}
          </DialogTitle>
          <DialogDescription className="text-xs text-neutral-400">
            {tess?.tessera_label} — {tess?.prezzo?.toFixed(0)} € · ID {tess?.id?.slice(0, 8)}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[["pagato", "Pagata — in lavorazione"], ["tessera_emessa", "Tessera emessa"]].map(([v, l]) => (
              <label
                key={v}
                data-testid={`stato-option-${v}`}
                className={`cursor-pointer border p-3 text-xs font-bold uppercase ${
                  form.stato === v ? "border-red-600 bg-red-950/30 text-white" : "border-neutral-800 bg-neutral-900 text-neutral-400"
                }`}
              >
                <input type="radio" name="stato" value={v} className="sr-only" checked={form.stato === v} onChange={() => setForm({ ...form, stato: v })} />
                {l}
              </label>
            ))}
          </div>
          <div>
            <label htmlFor="codice-coni" className={labelCls}>Codice identificativo CONI {form.stato === "tessera_emessa" && "*"}</label>
            <input
              id="codice-coni"
              data-testid="codice-coni-input"
              className={inputCls}
              placeholder="es. MSP-RN1-2026-000123"
              value={form.codice_coni}
              onChange={(e) => setForm({ ...form, codice_coni: e.target.value })}
              required={form.stato === "tessera_emessa"}
              maxLength={40}
            />
          </div>
          <div>
            <label htmlFor="note-segreteria" className={labelCls}>Note interne</label>
            <textarea id="note-segreteria" data-testid="note-segreteria-input" rows="3" className={inputCls} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={500} />
          </div>
          <button
            type="submit"
            data-testid="stato-save"
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-black uppercase tracking-widest transition-colors"
          >
            <BadgeCheck className="w-4 h-4" /> {saving ? "Salvataggio..." : "Salva stato pratica"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
